import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  ExternalLink, 
  QrCode,
  Link as LinkIcon,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { toast } from './ui/use-toast';

interface TrackedLink {
  id: number;
  originalUrl: string;
  shortLinks: number;
  clicksThisWeek: number;
  shortUrlBase: string;
}

export function LinkTracker() {
  // Mock data for links - replace with actual data fetching later
  const [links] = useState<TrackedLink[]>([
    { 
      id: 1, 
      originalUrl: 'bits.featrix.com/meet', 
      shortLinks: 72, 
      clicksThisWeek: 34, 
      shortUrlBase: 'alphaloop.example.com/xyz12' 
    },
    { 
      id: 2, 
      originalUrl: 'www.runalphaloop.com/', 
      shortLinks: 5, 
      clicksThisWeek: 721, 
      shortUrlBase: 'alphaloop.example.com/abc78' 
    },
  ]);

  const [expandedLinks, setExpandedLinks] = useState<Set<number>>(new Set());
  const [targetUrl, setTargetUrl] = useState('');
  const [generatedShortLink, setGeneratedShortLink] = useState('');
  const [showGeneratedLink, setShowGeneratedLink] = useState(false);

  const toggleLinkDetails = (linkId: number) => {
    const newExpanded = new Set(expandedLinks);
    if (newExpanded.has(linkId)) {
      newExpanded.delete(linkId);
    } else {
      newExpanded.add(linkId);
    }
    setExpandedLinks(newExpanded);
  };

  const generateShortLink = () => {
    if (!targetUrl.trim()) {
      toast({
        title: "URL required",
        description: "Please enter a target URL.",
        variant: "destructive"
      });
      return;
    }

    // Generate mock short link
    const mockShortLink = `alphaloop.example.com/${Math.random().toString(36).substring(2, 8)}`;
    setGeneratedShortLink(mockShortLink);
    setShowGeneratedLink(true);
    
    toast({
      title: "Short link generated",
      description: `Created: ${mockShortLink}`,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast({
          title: "Copied to clipboard",
          description: text,
        });
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        toast({
          title: "Copy failed",
          description: "Unable to copy to clipboard",
          variant: "destructive"
        });
      });
  };

  const generateQRCode = () => {
    toast({
      title: "QR Code",
      description: `QR code generation for ${generatedShortLink} coming soon!`,
    });
  };

  const generateAnotherLink = () => {
    setTargetUrl('');
    setShowGeneratedLink(false);
    setGeneratedShortLink('');
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center gap-3 mb-2">
          <LinkIcon className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Link Tracker</h1>
        </div>
        <p className="text-muted-foreground text-lg">
          Create trackable links and QR codes. Use the links in email, posts, posters, mailers to track engagement.
        </p>
      </div>

      {/* My Links Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            My Links
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {links.map((link) => (
            <div key={link.id} className="border rounded-lg p-4 space-y-3">
              {/* Link Item Main */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-foreground">{link.originalUrl}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <LinkIcon className="h-3 w-3" />
                      <span>{link.shortLinks} short links</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{link.clicksThisWeek} clicks this week</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{link.clicksThisWeek > 100 ? 'High Traffic' : 'Active'}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleLinkDetails(link.id)}
                    className="flex items-center gap-1"
                  >
                    Details
                    {expandedLinks.has(link.id) ? 
                      <ChevronUp className="h-4 w-4" /> : 
                      <ChevronDown className="h-4 w-4" />
                    }
                  </Button>
                </div>
              </div>

              {/* Expandable Link Details */}
              {expandedLinks.has(link.id) && (
                <div className="border-t pt-3 bg-muted/30 -mx-4 px-4 pb-2">
                  <h4 className="font-medium mb-3">Link Details for {link.originalUrl}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-muted-foreground">Short URL Base</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="bg-background px-2 py-1 rounded text-xs">{link.shortUrlBase}</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(link.shortUrlBase)}
                          className="h-6 w-6 p-0"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Usage Stats</Label>
                      <p className="mt-1 text-sm">Recent usage, analytics data, and performance metrics would appear here.</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <Label className="text-xs text-muted-foreground">Asset Connections</Label>
                    <p className="mt-1 text-sm text-muted-foreground">Connect to marketing campaigns, email sequences, or other assets (coming soon).</p>
                  </div>
                </div>
              )}
            </div>
          ))}

          {links.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No tracked links yet. Create your first link below!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create a Link Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Create a Link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="target-url">Target URL</Label>
            <Input
              id="target-url"
              type="url"
              placeholder="https://www.example.com"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="w-full"
            />
          </div>

          {showGeneratedLink && (
            <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
              <Label className="text-sm font-medium">Your Short Link</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-background px-3 py-2 rounded text-sm">
                  {generatedShortLink}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generatedShortLink)}
                  className="flex items-center gap-1"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            {!showGeneratedLink ? (
              <Button onClick={generateShortLink} className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Generate Short Link
              </Button>
            ) : (
              <>
                <Button onClick={generateAnotherLink} variant="outline">
                  Generate Another Link
                </Button>
                <Button onClick={generateQRCode} className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  Create QR Code
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 